/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { useCallback, useMemo } from 'react';
import { Purpose } from '../../managers/sso';
import { useUser } from '../account';
import type { OnOtpVerified, OtpFlow, OtpMountBehaviour } from './types';
import { type EmailFlowKey, EMAIL_OTP_PURPOSE_CONFIG } from './purposeConfig';
import { useDeleteAccountOtpFlow } from './email/useDeleteAccountOtpFlow';
import { useEmailOtpPurposeFlow } from './email/useEmailOtpPurposeFlow';
import { useEmailUpdateFlow } from '../useEmailUpdateFlow';
import { usePhoneNumberUpdateFlow } from '../usePhoneNumberUpdateFlow';
import { usePasswordlessOtpFlow } from './email/usePasswordlessOtpFlow';
import { useLogin2FaOtpFlow } from './email/useLogin2FaOtpFlow';

export interface UseVerifyEmailOtpFlowOptions {
  purpose: Purpose;
  email?: string;
  cancelDeletion?: boolean;
  startTimer: () => void;
  onVerified?: OnOtpVerified;
}

export interface UseVerifyEmailOtpFlowResult extends OtpFlow {
  resolvedEmail?: string;
  behaviour: OtpMountBehaviour;
}

export function useVerifyEmailOtpFlow({
  purpose,
  email,
  cancelDeletion = false,
  startTimer,
  onVerified,
}: UseVerifyEmailOtpFlowOptions): UseVerifyEmailOtpFlowResult {
  const { data: user } = useUser('useVerifyEmailOtpFlow');

  const resolvedEmail =
    email?.trim() || user?.username?.trim() || user?.email?.trim() || undefined;

  const isTerminalEmailPurpose =
    purpose === Purpose.PHONE_CHANGE ||
    purpose === Purpose.PASSWORD_CHANGE ||
    purpose === Purpose.LOGIN_2FA ||
    purpose === Purpose.UPDATE_2FA ||
    purpose === Purpose.FORGOT_PASSWORD;

  const emailUpdateFlow = useEmailUpdateFlow({
    onSuccess: () => {
      // Terminal purposes fire onVerified from their own worker instead.
      if (!isTerminalEmailPurpose) void onVerified?.();
    },
  });

  const requestThroughEmailUpdate = useCallback(() => {
    if (!resolvedEmail) return;
    emailUpdateFlow.requestEmailOtp(resolvedEmail, startTimer, startTimer);
  }, [emailUpdateFlow, resolvedEmail, startTimer]);

  const phoneNumberUpdateFlow = usePhoneNumberUpdateFlow();

  const passwordlessOtpFlow = usePasswordlessOtpFlow({
    email: resolvedEmail,
    startTimer,
    onVerified,
    isSignup: purpose === Purpose.EMAIL_OTP_VERIFICATION_SIGNUP,
    cancelDeletion,
  });

  const login2FaOtpFlow = useLogin2FaOtpFlow({
    startTimer,
    onVerified,
  });

  const deleteAccountFlow = useDeleteAccountOtpFlow({
    email: resolvedEmail,
    startTimer,
    onVerified,
  });

  const purposeOtpFlow = useEmailOtpPurposeFlow({
    email: resolvedEmail,
    otpPurpose: purpose,
    startTimer,
    onVerified: (data) =>
      void onVerified?.({
        emailOtpAuthToken:
          purpose === Purpose.UPDATE_2FA ? data?.emailOtpAuthToken : undefined,
      }),
  });

  const emailChangeFlow = useMemo<OtpFlow>(
    () => ({
      submitOtp: (otp: string) => {
        if (!resolvedEmail) return;
        emailUpdateFlow.verifyEmailOtp(resolvedEmail, otp);
      },
      requestOtp: requestThroughEmailUpdate,
      errorMessage: emailUpdateFlow.errorMessage,
      isLoading: emailUpdateFlow.isLoading,
      clearError: emailUpdateFlow.clearError,
    }),
    [emailUpdateFlow, requestThroughEmailUpdate, resolvedEmail],
  );

  const phoneChangeFlow = useMemo<OtpFlow>(
    () => ({
      submitOtp: (otp: string) =>
        phoneNumberUpdateFlow.verifyEmailOtp(otp, onVerified),
      requestOtp: () =>
        phoneNumberUpdateFlow.requestEmailOtp(startTimer, startTimer),
      errorMessage: phoneNumberUpdateFlow.errorMessage,
      errorCode: phoneNumberUpdateFlow.errorCode,
      isLoading: phoneNumberUpdateFlow.isLoading,
      clearError: phoneNumberUpdateFlow.clearError,
    }),
    [onVerified, phoneNumberUpdateFlow, startTimer],
  );

  const workers: Record<EmailFlowKey, OtpFlow> = {
    passwordless: passwordlessOtpFlow,
    login2fa: login2FaOtpFlow,
    delete: deleteAccountFlow,
    phoneChange: phoneChangeFlow,
    purpose: purposeOtpFlow,
    emailChange: emailChangeFlow,
  };

  const { worker, behaviour } =
    EMAIL_OTP_PURPOSE_CONFIG[purpose] ??
    EMAIL_OTP_PURPOSE_CONFIG[Purpose.USERNAME_CHANGE];

  return { ...workers[worker], resolvedEmail, behaviour };
}
